import { useNavigate } from "react-router-dom";
import CoachShell from "@/pages/RehearsalRoom/CoachShell";
import { Badge } from "@/components/ui/badge";
import { useTeamOverview } from "@/lib/teamRecordQueries";
import OverviewTab from "./tabs/OverviewTab";
import MembersTab from "./tabs/MembersTab";
import PrepTab from "./tabs/PrepTab";
import SessionsTab from "./tabs/SessionsTab";
import PracticeLayersTab from "./tabs/PracticeLayersTab";
import JourneyConfigTab from "./tabs/JourneyConfigTab";
import FilesVideosTab from "./tabs/FilesVideosTab";
import MessagesTab from "./tabs/MessagesTab";
import TimelineTab from "./tabs/TimelineTab";
import AccessSettingsTab from "./tabs/AccessSettingsTab";

const TABS = [
  ["overview", "Overview"], ["members", "Members"], ["prep", "Prep"], ["sessions", "Sessions"],
  ["practice", "Practice & Layers"], ["journey", "Journey Configuration"], ["files", "Files & Videos"],
  ["messages", "Messages"], ["timeline", "Timeline"], ["access-settings", "Access & Settings"],
];

const TAB_COMPONENTS = {
  overview: OverviewTab, members: MembersTab, prep: PrepTab, sessions: SessionsTab,
  practice: PracticeLayersTab, journey: JourneyConfigTab, files: FilesVideosTab,
  messages: MessagesTab, timeline: TimelineTab, "access-settings": AccessSettingsTab,
};

/**
 * Clients Restructure — Team 360. The TEAM-owned counterpart of
 * Client 360 (client360/Client360Page.jsx) — same visual language and
 * shell, scoped by Workspace (access_type='team') instead of one
 * member. Every tab here only ever mutates TEAM-owned data; an
 * individual member's own data is always edited from their Client 360.
 */
export default function Team360Page({ workspaceId, tab }) {
  const navigate = useNavigate();
  const { data, isPending, isError } = useTeamOverview(workspaceId);
  const ActiveTab = TAB_COMPONENTS[tab] || OverviewTab;

  return (
    <CoachShell>
      <div data-testid="team-360-page">
        <button
          onClick={() => navigate("/rehearsal-room/coach/clients")}
          className="text-xs text-ink-soft hover:text-ink mb-3 transition-colors"
          data-testid="team-360-back-to-clients"
        >
          ← Clients
        </button>

        {isPending ? (
          <p className="text-sm text-ink-soft" data-testid="team-360-loading">Loading team record…</p>
        ) : isError ? (
          <p className="text-sm text-red-600" data-testid="team-360-error">Couldn't load this team record.</p>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <h1 className="font-serif text-2xl text-ink mb-1" data-testid="team-360-team-name">{data.team_name}</h1>
                <div className="flex items-center gap-2 text-sm text-ink-soft">
                  <span data-testid="team-360-member-count">{data.member_count} member{data.member_count === 1 ? "" : "s"}</span>
                  <Badge variant="outline" className="uppercase text-[10px] tracking-wide" data-testid="team-360-status">{data.status_bucket}</Badge>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-6 flex-wrap border-b border-ink/10 pb-3 overflow-x-auto" data-testid="team-360-tabs">
              {TABS.map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => navigate(`/rehearsal-room/coach/team/${workspaceId}/${key}`)}
                  className={`text-sm px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${tab === key ? "bg-ink text-paper" : "text-ink-soft hover:bg-ink/5"}`}
                  data-testid={`team-360-tab-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>

            <ActiveTab workspaceId={workspaceId} overview={data} />
          </>
        )}
      </div>
    </CoachShell>
  );
}
